import type { ReactNode } from 'react';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';

type FontSize = 'small' | 'medium' | 'large';

interface FontSizeContextType {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(
  undefined
);

const FONT_SIZE_STORAGE_KEY = 'qler-font-size-preference';

const fontSizeScales = {
  small: 1,
  medium: 1.15,
  large: 1.25,
};

export const FontSizeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    const stored = localStorage.getItem(FONT_SIZE_STORAGE_KEY);
    return (stored as FontSize) || 'small';
  });

  useEffect(() => {
    const scale = fontSizeScales[fontSize];
    document.documentElement.style.setProperty(
      '--font-scale',
      scale.toString()
    );
    localStorage.setItem(FONT_SIZE_STORAGE_KEY, fontSize);
  }, [fontSize]);

  const value = useMemo(
    () => ({ fontSize, setFontSize }),
    [fontSize, setFontSize]
  );

  return (
    <FontSizeContext.Provider value={value}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = (): FontSizeContextType => {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};
